import { useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Activity, ShieldCheck } from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { usePermissions } from '../../../hooks/prioritization/usePermissions';
import { UI_LABELS } from '../../../lib/terminology';
import { DecisionHealthDashboard } from './DecisionHealthDashboard';

type AnalyticsTab = 'overview' | 'decision-health';

export function Analytics() {
  const [tab, setTab] = useState<AnalyticsTab>('overview');
  const itemsFromStore = useItemsStore((state) => state.items);
  const getUnreadyItems = useItemsStore((state) => state.getUnreadyItems);
  const getItems = useItemsStore((state) => state.getItems);
  const getPriorities = useItemsStore((state) => state.getPriorities);

  const itemPools = useItemPoolsStore((state) => state.itemPools);
  const driftEvents = useItemPoolsStore((state) => state.driftEvents);

  const permissions = usePermissions();

  const unreadyItems = getUnreadyItems();
  const items = getItems();
  const priorities = getPriorities();
  
  // Calculate metrics
  const totalAboveLine = itemPools.reduce((sum, list) => sum + list.cutLinePosition, 0);
  const totalBelowLine = itemPools.reduce((sum, list) => 
    sum + (list.priorityIds.length - list.cutLinePosition), 0
  );
  
  const unresolvedDrift = driftEvents.filter(e => !e.resolved).length;
  const resolvedDrift = driftEvents.filter(e => e.resolved).length;
  
  const avgPrioritiesPerList = itemPools.length > 0 
    ? (priorities.length / itemPools.length).toFixed(1)
    : 0;
  
  const shapingCompletionRate = items.length > 0
    ? ((items.filter(i => i.readyForRanking).length / items.length) * 100).toFixed(0)
    : 0;
  
  const lifecycleDistribution = [
    { state: 'Intake', count: unreadyItems.length, color: 'bg-[var(--mustard)]', textColor: 'text-[var(--mustard)]' },
    { state: 'Item', count: items.length, color: 'bg-[var(--mustard)]', textColor: 'text-[var(--mustard)]' },
    { state: 'In pool', count: priorities.length, color: 'bg-[var(--green)]', textColor: 'text-[var(--green)] dark:text-[var(--text-ter)]' }
  ];
  
  const totalItems = itemsFromStore.length;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="h-8 w-8 text-[var(--text-sec)]" />
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">Analytics & Insights</h1>
        </div>
        <p className="text-[var(--text-sec)]">
          Analyze prioritization patterns, list health, velocity, and drift trends
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[var(--rule)]">
        {([
          { id: 'overview', label: 'Overview', Icon: BarChart3 },
          { id: 'decision-health', label: 'Decision Health', Icon: ShieldCheck },
        ] as { id: AnalyticsTab; label: string; Icon: React.ElementType }[]).map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'decision-health' && <DecisionHealthDashboard />}
      {tab === 'overview' && (<div className="space-y-6">

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total Items</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">{totalItems}</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-[var(--text-ter)]">
            <Activity className="h-3 w-3" />
            <span>Across all states</span>
          </div>
        </div>
        
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">{UI_LABELS.itemPools}</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">{itemPools.length}</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-[var(--text-ter)]">
            <span>Avg {avgPrioritiesPerList} priorities/list</span>
          </div>
        </div>
        
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Item Work Rate</p>
          <p className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{shapingCompletionRate}%</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-[var(--text-ter)]">
            <TrendingUp className="h-3 w-3" />
            <span>Items meeting prerequisites</span>
          </div>
        </div>
        
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Drift Events</p>
          <p className={`text-3xl font-bold ${unresolvedDrift > 0 ? 'text-[var(--coral)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}`}>
            {unresolvedDrift}
          </p>
          <div className="flex items-center gap-1 mt-2 text-xs text-[var(--text-ter)]">
            <span>{resolvedDrift} resolved</span>
          </div>
        </div>
      </div>
      
      {/* Lifecycle Distribution */}
      <div className="card">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          Lifecycle Distribution
        </h2>
        
        <div className="space-y-4">
          {lifecycleDistribution.map((item) => {
            const percentage = totalItems > 0 ? (item.count / totalItems) * 100 : 0;
            
            return (
              <div key={item.state}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium ${item.textColor}`}>{item.state}</span>
                  <span className="text-sm text-[var(--text-sec)]">{item.count} ({percentage.toFixed(0)}%)</span>
                </div>
                <div className="w-full bg-[var(--hover-tint)] rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${item.color} transition-all`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Above/Below the Line Analysis */}
      <div className="card">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          Above/Below the Line
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 bg-[var(--green)] rounded" />
              <span className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Above the Line</span>
            </div>
            <p className="text-4xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{totalAboveLine}</p>
            <p className="text-sm text-[var(--text-sec)] mt-1">Active priorities being worked on</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 bg-[var(--surface)] rounded" />
              <span className="font-medium text-[var(--text-sec)]">Below the Line</span>
            </div>
            <p className="text-4xl font-bold text-[var(--text-sec)]">{totalBelowLine}</p>
            <p className="text-sm text-[var(--text-sec)] mt-1">Deferred priorities</p>
          </div>
        </div>
      </div>
      
      {/* List Health */}
      <div className="card">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          List Health Indicators
        </h2>
        
        <div className="space-y-3">
          {itemPools.map((list) => {
            const driftCount = driftEvents.filter(e => e.itemPoolId === list.id && !e.resolved).length;
            const lastRankedDays = list.lastRankedAt 
              ? Math.floor((Date.now() - new Date(list.lastRankedAt).getTime()) / (1000 * 60 * 60 * 24))
              : Math.floor((Date.now() - new Date(list.createdAt).getTime()) / (1000 * 60 * 60 * 24));
            const isStale = lastRankedDays > 14;
            const hasIssues = driftCount > 0 || isStale;
            
            return (
              <div
                key={list.id}
                className={`p-4  border ${
                  hasIssues ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]' : 'border-[var(--green)] bg-[var(--green-dim)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-[var(--text-pri)]">{list.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className={driftCount > 0 ? 'text-[var(--coral)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                        {driftCount} drift events
                      </span>
                      <span className={isStale ? 'text-[var(--mustard)]' : 'text-[var(--text-sec)]'}>
                        Last ranked: {lastRankedDays}d ago
                      </span>
                      <span className="text-[var(--text-sec)]">
                        {list.priorityIds.length} priorities
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {hasIssues ? (
                      <TrendingDown className="h-6 w-6 text-[var(--mustard)]" />
                    ) : (
                      <TrendingUp className="h-6 w-6 text-[var(--green)]" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {itemPools.length === 0 && (
            <div className="text-center py-8 text-[var(--text-ter)]">
              <p>No {UI_LABELS.itemPools.toLowerCase()} to analyze</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Insights */}
      <div className="card bg-[var(--surface)]">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          System Insights
        </h2>
        
        <div className="space-y-3 text-sm">
          <div className="p-3 bg-[var(--bg-raised)] border border-[var(--rule)]">
            <p className="font-medium text-[var(--text-pri)]">Candidate Flow</p>
            <p className="text-[var(--text-sec)] mt-1">
              {unreadyItems.length > items.length + priorities.length && (
                <>Intake is building up - consider adding item work capacity</>
              )}
              {unreadyItems.length === 0 && items.length > 0 && (
                <>All intake items have been through item work - good flow through intake</>
              )}
              {items.length > priorities.length && (
                <>Many items waiting for ranking - schedule a prioritization session</>
              )}
            </p>
          </div>
          
          {unresolvedDrift > 0 && (
            <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)]">
              <p className="font-medium text-[var(--coral)]">Alignment Warning</p>
              <p className="text-[var(--coral)] mt-1">
                {unresolvedDrift} unresolved drift event{unresolvedDrift > 1 ? 's' : ''} detected - 
                review and realign to maintain focus integrity
              </p>
            </div>
          )}
          
          {totalBelowLine > totalAboveLine && (
            <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <p className="font-medium text-[var(--mustard)]">Capacity Signal</p>
              <p className="text-[var(--mustard)] mt-1">
                More priorities below the line than above - consider reviewing cut line positions 
                or reducing list sizes
              </p>
            </div>
          )}
        </div>
      </div>
      
      {!permissions.canViewAnalytics && (
        <div className="card bg-[var(--surface)] border-[var(--rule)] text-center py-8">
          <p className="text-sm text-[var(--text-sec)]">
            You don't have permission to view detailed analytics
          </p>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Switch to Strategist, Cross-Map Lead, or higher tier role
          </p>
        </div>
      )}
      </div>)}
    </div>
  );
}






